import React from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  useColorMode,
  VStack,
} from "@chakra-ui/react";
import useToastNotification from "../../hooks/useToastNotification";

const convertEditableDataToCsv = (data) => {
  if (!data || data.length === 0) return "";
  return data
    .map((row) =>
      row
        .map((cell) => {
          const cellStr = String(cell ?? "");
          if (
            cellStr.includes(",") ||
            cellStr.includes('"') ||
            cellStr.includes("\n")
          ) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(",")
    )
    .join("\n");
};

const renderObjectTable = ({
  rows,
  title,
  titleColor,
  headerBg,
  borderColor,
  cellTextColor,
  colorMode,
}) => {
  const allKeys = new Set();
  rows.forEach((row) => {
    if (typeof row === "object" && row !== null) {
      Object.keys(row).forEach((k) => allKeys.add(k));
    }
  });
  const headers = Array.from(allKeys);

  return (
    <Box>
      <Text fontWeight="bold" mb={2} fontSize="sm" color={titleColor}>
        {title}
      </Text>
      <TableContainer
        borderWidth="1px"
        borderRadius="md"
        borderColor={borderColor}
        maxH="300px"
        overflowY="auto"
        overflowX="auto"
        width="fit-content"
      >
        <Table size="sm" variant="simple">
          <Thead position="sticky" top={0} bgColor={headerBg} zIndex={1}>
            <Tr>
              <Th width="50px">#</Th>
              {headers.map((header, idx) => (
                <Th key={idx} textTransform="capitalize" whiteSpace="nowrap">
                  {header.replace(/_/g, " ")}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, idx) => (
              <Tr key={idx}>
                <Td isNumeric>{idx + 1}</Td>
                {headers.map((header, headerIdx) => (
                  <Td key={headerIdx} whiteSpace="nowrap">
                    <Text fontSize="sm" color={cellTextColor}>
                      {row[header] !== null && row[header] !== undefined
                        ? typeof row[header] === "object"
                          ? JSON.stringify(row[header])
                          : String(row[header])
                        : "-"}
                    </Text>
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const CsvFixResultsDialog = ({
  isOpen,
  cancelRef,
  csvFixResult,
  fixedCsvContent,
  editableCsvData,
  onEditableCsvChange,
  selectedFile,
  csvFixIsBulkUpdate,
  onClose,
  onResetFixState,
  onImport,
  onBulkUpdate,
}) => {
  const { colorMode } = useColorMode();
  const showToast = useToastNotification();

  const handleDownload = () => {
    const csvToDownload = editableCsvData
      ? convertEditableDataToCsv(editableCsvData)
      : fixedCsvContent;
    const blob = new Blob([csvToDownload], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const originalName = selectedFile?.name || "fixed_trades";
    const nameWithoutExt = originalName.replace(/\.[^/.]+$/, "");
    link.download = `${nameWithoutExt}_fixed.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showToast("Download Started", {
      description: "Fixed CSV file is downloading (includes your edits).",
      status: "info",
      duration: 2000,
    });
  };

  const buildFixedFile = () => {
    const csvToImport = editableCsvData
      ? convertEditableDataToCsv(editableCsvData)
      : fixedCsvContent;
    const blob = new Blob([csvToImport], { type: "text/csv" });
    return new File([blob], selectedFile?.name || "fixed.csv", {
      type: "text/csv",
    });
  };

  const runConfirm = async ({ withRemainingIssues }) => {
    const fixedFile = buildFixedFile();
    onClose();
    try {
      if (csvFixIsBulkUpdate) {
        await onBulkUpdate(fixedFile);
        showToast("Update Successful", {
          description: withRemainingIssues
            ? "Fixed CSV has been updated. Please review any remaining issues."
            : "Fixed CSV has been updated successfully (includes your edits).",
          status: "success",
          duration: 3000,
        });
      } else {
        await onImport(fixedFile);
        showToast("Import Successful", {
          description: withRemainingIssues
            ? "Fixed CSV has been imported. Please review any remaining issues."
            : "Fixed CSV has been imported successfully (includes your edits).",
          status: "success",
          duration: 3000,
        });
      }
    } finally {
      onResetFixState();
    }
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      size="xl"
    >
      <AlertDialogOverlay>
        <AlertDialogContent maxW="90vw" w="fit-content">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            CSV Fix Results
          </AlertDialogHeader>
          <AlertDialogBody>
            {csvFixResult && (
              <VStack spacing={4} align="flex-start" maxH="70vh" overflowY="auto">
                <HStack spacing={6} align="center">
                  <Box>
                    <Text fontWeight="bold" mb={1} fontSize="sm">Format Detected:</Text>
                    <Badge
                      colorScheme={
                        csvFixResult.format_detected === "unknown" ? "gray" : "blue"
                      }
                      fontSize="sm"
                      px={2}
                      py={1}
                    >
                      {csvFixResult.format_detected || "unknown"}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" mb={1} fontSize="sm">Auto-fix Status:</Text>
                    <Badge
                      colorScheme={csvFixResult.can_auto_fix ? "green" : "orange"}
                      fontSize="sm"
                      px={2}
                      py={1}
                    >
                      {csvFixResult.can_auto_fix
                        ? "All issues can be auto-fixed"
                        : "Some issues require manual attention"}
                    </Badge>
                  </Box>
                </HStack>

                {csvFixResult.fixes_applied?.length > 0 &&
                  renderObjectTable({
                    rows: csvFixResult.fixes_applied,
                    title: "Fixes Applied:",
                    headerBg: colorMode === "light" ? "gray.100" : "gray.700",
                    borderColor: colorMode === "light" ? "gray.200" : "gray.600",
                    colorMode,
                  })}

                {csvFixResult.issues_found?.length > 0 &&
                  renderObjectTable({
                    rows: csvFixResult.issues_found,
                    title: "Issues Found:",
                    headerBg: colorMode === "light" ? "orange.50" : "orange.900",
                    borderColor: colorMode === "light" ? "gray.200" : "gray.600",
                    cellTextColor: "orange.500",
                    colorMode,
                  })}

                {csvFixResult.remaining_issues?.length > 0 &&
                  renderObjectTable({
                    rows: csvFixResult.remaining_issues,
                    title: "Remaining Issues (Require Manual Attention):",
                    titleColor: "red.500",
                    headerBg: colorMode === "light" ? "red.50" : "red.900",
                    borderColor: colorMode === "light" ? "red.200" : "red.800",
                    cellTextColor: "red.500",
                    colorMode,
                  })}

                {editableCsvData && editableCsvData.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2} fontSize="sm">
                      Fixed CSV Preview (Editable):
                    </Text>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      You can edit values directly in the table. Changes will be used when importing or downloading.
                    </Text>
                    <TableContainer
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor={colorMode === "light" ? "gray.200" : "gray.600"}
                      maxH="300px"
                      overflowY="auto"
                      overflowX="auto"
                      width="fit-content"
                    >
                      <Table size="sm" variant="simple" fontFamily="mono">
                        <Thead
                          position="sticky"
                          top={0}
                          bgColor={colorMode === "light" ? "gray.100" : "gray.700"}
                          zIndex={1}
                        >
                          {editableCsvData.length > 0 && (
                            <Tr>
                              {editableCsvData[0].map((header, idx) => (
                                <Th
                                  key={idx}
                                  fontSize="xs"
                                  whiteSpace="nowrap"
                                  borderWidth="1px"
                                >
                                  {header}
                                </Th>
                              ))}
                            </Tr>
                          )}
                        </Thead>
                        <Tbody>
                          {editableCsvData.slice(1).map((row, rowIdx) => (
                            <Tr key={rowIdx}>
                              {row.map((cell, cellIdx) => (
                                <Td
                                  key={cellIdx}
                                  fontSize="xs"
                                  whiteSpace="nowrap"
                                  borderWidth="1px"
                                  p={1}
                                >
                                  <Input
                                    size="xs"
                                    value={cell}
                                    onChange={(e) => {
                                      const newData = [...editableCsvData];
                                      newData[rowIdx + 1][cellIdx] = e.target.value;
                                      onEditableCsvChange(newData);
                                    }}
                                    fontFamily="mono"
                                    fontSize="xs"
                                    px={1}
                                    py={0}
                                    h="20px"
                                  />
                                </Td>
                              ))}
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {!fixedCsvContent && (
                  <Box>
                    <Text color="orange.500" fontWeight="bold">
                      No fixed CSV content available. Please review the issues above and fix them manually.
                    </Text>
                  </Box>
                )}
              </VStack>
            )}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Close
            </Button>
            {editableCsvData && editableCsvData.length > 0 && (
              <Button
                colorScheme="blue"
                variant="outline"
                ml={3}
                onClick={handleDownload}
              >
                Download Fixed CSV
              </Button>
            )}
            {editableCsvData &&
              editableCsvData.length > 0 &&
              csvFixResult &&
              csvFixResult.can_auto_fix && (
                <Button
                  colorScheme="green"
                  ml={3}
                  onClick={() => runConfirm({ withRemainingIssues: false })}
                >
                  {csvFixIsBulkUpdate ? "Update Fixed CSV" : "Import Fixed CSV"}
                </Button>
              )}
            {editableCsvData &&
              editableCsvData.length > 0 &&
              csvFixResult &&
              !csvFixResult.can_auto_fix && (
                <Button
                  colorScheme="orange"
                  ml={3}
                  onClick={() => runConfirm({ withRemainingIssues: true })}
                >
                  {csvFixIsBulkUpdate
                    ? "Update Anyway (Review Issues First)"
                    : "Import Anyway (Review Issues First)"}
                </Button>
              )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default CsvFixResultsDialog;
